import { CategorySelect } from "@/components/categories/CategorySelect";
import { useCategories } from "@/contexts/CategoriesContext";

const LABEL = "font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1";
const FIELD = "w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none";
const TYPE_LABELS = { expense: "Expenditure", income: "Income", transfer: "Transfer", investment: "Investment" };

function AccountSelect({ label, value, onChange, accounts, testid, excludeId }) {
  return (
    <label className="block mb-4">
      <span className={LABEL}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={FIELD}>
        <option value="">— none —</option>
        {accounts.length === 0 && <option value="" disabled>No accounts yet — add one on the Accounts page</option>}
        {accounts.filter((a) => a.id !== excludeId).map((a) => <option key={a.id} value={a.id}>{a.name} ({a.type})</option>)}
      </select>
    </label>
  );
}

/** Create/edit transaction modal. Renders nothing while closed. */
export function TransactionFormModal({ open, editingId, form, setForm, accounts, onSubmit, onClose }) {
  const lists = useCategories();
  if (!open) return null;
  const isTransfer = form.type === "transfer";
  const pickType = (t) => setForm({ ...form, type: t, category: (lists[t] && lists[t][0]) || "Other" });
  return (
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-50 flex items-center justify-center p-6 fade-in" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="transaction-form">
        <h3 className="font-serif text-3xl mb-6 tracking-tight">{editingId ? "Edit entry" : "New entry"}</h3>

        <div className="grid grid-cols-2 gap-2 mb-6">
          {Object.entries(TYPE_LABELS).map(([t, label]) => (
            <button
              type="button"
              key={t}
              data-testid={`txn-type-${t}`}
              onClick={() => pickType(t)}
              className={`py-2 text-xs uppercase tracking-widest border ${form.type === t ? "bg-[#1A1F16] text-[#F7F5F0] border-[#1A1F16]" : "border-[#1A1F16]"}`}
            >{label}</button>
          ))}
        </div>

        <label className="block mb-4">
          <span className={LABEL}>Amount (₹)</span>
          <input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="txn-amount" className="w-full bg-transparent border-b border-[#1A1F16] py-2 text-2xl font-serif num focus:outline-none focus:border-[#A67C00]" />
        </label>

        <label className="block mb-4">
          <span className={LABEL}>Category</span>
          <CategorySelect type={form.type} value={form.category} onChange={(c) => setForm({ ...form, category: c })} testid="txn-category" className={FIELD} />
        </label>

        <AccountSelect
          label={isTransfer ? "From account" : "Account (optional)"}
          value={form.account_id}
          onChange={(v) => setForm({ ...form, account_id: v })}
          accounts={accounts}
          testid="txn-account"
          excludeId={isTransfer ? form.to_account_id : null}
        />
        {isTransfer && (
          <AccountSelect
            label="To account"
            value={form.to_account_id}
            onChange={(v) => setForm({ ...form, to_account_id: v })}
            accounts={accounts}
            testid="txn-to-account"
            excludeId={form.account_id}
          />
        )}

        <label className="block mb-4">
          <span className={LABEL}>Date</span>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="txn-date" className={FIELD} />
        </label>

        <label className="block mb-6">
          <span className={LABEL}>Note</span>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="txn-description" className={FIELD} />
        </label>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 border border-[#1A1F16] py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD]">Cancel</button>
          <button type="submit" data-testid="txn-submit" className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628]">{editingId ? "Update" : "Record"}</button>
        </div>
      </form>
    </div>
  );
}
